import React, {Component} from 'react';
import PropTypes from 'prop-types';

import IconButton from 'material-ui/IconButton';
import FontIcon from 'material-ui/FontIcon';
import Divider from 'material-ui/Divider';
import { Toolbar, ToolbarGroup } from 'material-ui/Toolbar';
import RaisedButton from 'material-ui/RaisedButton';
import TextField from 'material-ui/TextField';
import { IconMenu, MenuItem } from 'material-ui/IconMenu';
import { ListItem } from 'material-ui/List';
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderColumn,
  TableRow,
  TableRowColumn,
} from 'material-ui/Table';

import AppConstants from '../../constants/AppConstants';
import { confirmDialog, notifyBar } from '../../util/dialogs';
import { renderAvatarUrl, renderTimestampAsDate as renderDate} from '../../util/renderHelpers';
import EditUserAccountDialog from '../EditUserAccountDialog';
import HubAvatar from '../HubAvatar';
import GoogleLogo from '../../media/google_logo.svg';

const HubFontIcon = ({children, ...rest}) => (
  <FontIcon color={AppConstants.colors.iconColor} hoverColor={AppConstants.colors.iconHoverColor} {...rest}>{children}</FontIcon>
)

HubFontIcon.propTypes = {
  children: PropTypes.any,
}

const iconSearch = <HubFontIcon className="material-icons">search</HubFontIcon>
const iconRefresh = <HubFontIcon className="material-icons">refresh</HubFontIcon>
const iconEmail = <FontIcon color={AppConstants.colors.iconColor} className="material-icons">email</FontIcon>
const iconGoogle = <FontIcon><img src={GoogleLogo} alt=""/></FontIcon>
const iconMoreVert = <HubFontIcon className="material-icons">more_vert</HubFontIcon>

class UserTable extends Component {
  static propTypes = {
    userStore: PropTypes.object.isRequired,
    users: PropTypes.array.isRequired,
    onAddUserClicked: PropTypes.func,
    showClientColumn: PropTypes.bool,
    showTopBar: PropTypes.bool,
  }
  
  constructor(props){
    super(props);
    this.userStore = props.userStore;
    this.state = {
      selectedUser: props.users[0],
      showEditForm: false,
      userFilter: '',
    }
  }

  handleRefresh = () => {
    this.userStore.refreshUserList();
  }

  handleSearchInput = (event, value) => {
    this.setState({userFilter:value});
  }

  handleEditAccountClicked = (user) => {
    this.setState({
      selectedUser: user,
      showEditForm: true,
    });
  }

  handleRequestDialogClose = () => {
    this.setState({
      showEditForm: false,
    });
  }

  handleEditUserSave = (user) => {
    this.handleRequestDialogClose();
    // Do stuff to create the user here.
    let { selectedUser } = this.state;
    if (!selectedUser.client) {
      confirmDialog(`You assigned the user to a client. Send out an automated confirmation mail to ${user.email}?`)
        .then(shouldMail => {
          this.updateUser(user, shouldMail);
        });
    } else {
      this.updateUser(user, false);
    }
  }

  updateUser = (user, shouldMail) => {
    this.userStore.update(user, shouldMail).then(() => {
      notifyBar('User updated.');
    });
  }


  handleDisableUser = (user) => {
    this.userStore.disable(user).then(() => {
      notifyBar(`User: \`${user.email}\` disabled.`);
    });
  }

  handleEnableUser = (user) => {
    this.userStore.enable(user).then(() => {
      notifyBar(`User: \`${user.email}\` enabled.`);
    });
  }

  handleDeleteUser = (user) => {
    this.userStore.delete(user).then(() => {
      notifyBar('User deleted.');
    });
  }

  userMatchesFilter = (user, filter) => {
    let {displayName, email, uid, client} = user;
    if (!filter) {
      return true;
    }

    let regexFilter = new RegExp(filter, "gi");
    if (
      (displayName && displayName.match(regexFilter) !== null) ||
      (client && client.match(regexFilter) !== null) ||
      (email && email.match(regexFilter) !== null) ||
      (uid && uid.match(regexFilter) !== null)
    ) {
      return true;
    }
    return false;
  }

  renderToolbar() {
    let searchBar = { width: '75%' };
    return (
      <div>
        <Toolbar>
          <ToolbarGroup style={searchBar}>
            <TextField
              fullWidth={true}
              hintText={<span>{iconSearch} Search by name, email, client or UID</span>}
              onChange={this.handleSearchInput}
            />
          </ToolbarGroup>
          <ToolbarGroup>
            <RaisedButton primary={true} label="Add user" onTouchTap={this.props.onAddUserClicked} />
            <IconButton onTouchTap={this.handleRefresh}>{iconRefresh}</IconButton>
          </ToolbarGroup>
        </Toolbar>
        <Divider />
      </div>
    )
  }

  render() {
    const { showClientColumn = true, showTopBar, users } = this.props;
    const { userFilter } = this.state;
    const styles = {
      noEllipsis: {
        textOverflow: 'none',
      },
      menuCell: {
        ...this.noEllipsis,
        paddingLeft: 0,
        paddingRight: 0,
        textAlign: 'center',
      },
      tableHeader: {
        backgroundColor: AppConstants.colors.accent2Color,
      },
      centeredTable: {
        ...this.noEllipsis,
        textAlign: 'center',
        paddingLeft: 0,
        paddingRight: 0,
      },
      userListItem: {
        marginLeft: 0,
        fontSize: '14px',
        lineHeight: '14px',
      },
    }

    const toolbar = showTopBar ? this.renderToolbar() : null;
    return (
      <div>
        {
          this.state.showEditForm && 
          <EditUserAccountDialog
            user={this.state.selectedUser}
            onRequestClose={this.handleRequestDialogClose}
            onSave={this.handleEditUserSave}
          />
        }
        {toolbar}
        <Table selectable={false}>
          <TableHeader
            displaySelectAll={false}
            adjustForCheckbox={false}
            style={styles.tableHeader}
          >
            <TableRow>
              <TableHeaderColumn colSpan="5">Name/Email</TableHeaderColumn>
              {showClientColumn && <TableHeaderColumn colSpan="2">Client</TableHeaderColumn>}
              <TableHeaderColumn style={styles.centeredTable}>Providers</TableHeaderColumn>
              <TableHeaderColumn colSpan="2">Created</TableHeaderColumn>
              <TableHeaderColumn colSpan="2">Last Sign-In</TableHeaderColumn>
              <TableHeaderColumn colSpan="4">User UID</TableHeaderColumn>
              <TableHeaderColumn>{/*row actions*/}</TableHeaderColumn>
            </TableRow>
          </TableHeader>
          <TableBody displayRowCheckbox={false} showRowHover={true}>
            {users.map((user, index) => {
              if (!this.userMatchesFilter(user, userFilter)) {
                return null;
              }
              return (
                <TableRow key={index} style={{opacity: user.disabled ? 0.5 : 1, }}>
                  <TableRowColumn colSpan="5">
                    <ListItem
                      style={styles.userListItem}
                      disabled={true}
                      leftAvatar={<HubAvatar src={renderAvatarUrl(user.photoUrl)} />}
                      primaryText={user.displayName || user.email}
                      secondaryText={!!user.displayName && user.email}
                    />
                  </TableRowColumn>
                  {
                    showClientColumn &&
                    <TableRowColumn colSpan="2" title={user.client}>
                      {user.client}
                    </TableRowColumn>
                  }
                  <TableRowColumn
                    style={styles.centeredTable}
                    title={user.providerUserInfo && user.providerUserInfo[0].providerId}
                  >
                    {user.providerUserInfo && user.providerUserInfo[0].providerId === 'google.com' ? iconGoogle : iconEmail}
                  </TableRowColumn>
                  <TableRowColumn colSpan="2" title={renderDate(user.createdAt)}>
                    {renderDate(user.createdAt)}
                  </TableRowColumn>
                  <TableRowColumn colSpan="2" title={renderDate(user.lastLoginAt)}>
                    {renderDate(user.lastLoginAt)}
                  </TableRowColumn>
                  <TableRowColumn colSpan="4" title={user.uid}>{user.uid}</TableRowColumn>
                  <TableRowColumn style={styles.menuCell}>
                    <ContextMenu
                      onEditAccountClicked={this.handleEditAccountClicked}
                      onEnableUser={this.handleEnableUser}
                      onDisableUser={this.handleDisableUser}
                      onDeleteUser={this.handleDeleteUser}
                      user={user}/>
                  </TableRowColumn>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    )
  }
}

const ContextMenu = ({ user, onEditAccountClicked, onEnableUser, onDisableUser, onDeleteUser,}) => {

  const handleEditAccountClicked = () => {
    onEditAccountClicked(user);
  }
  const handleEnableUser = () => {
    onEnableUser(user);
  }
  const handleDisableUser = () => {
    onDisableUser(user);
  }
  const handleDelete = () => {
    confirmDialog(`Are you sure you wish to delete poor old ${user.displayName||user.email}`)
      .then(del => {
        if (del) {
          onDeleteUser(user);
        }
      });
  }

  const openGoogleAnalytics = () => {
    window.open(`https://analytics.google.com/analytics/web/#report/visitors-user-activity/a102444222w149986982p155020735/%3F_r.userId%3D${user.uid}`);
  }

  return (
    <IconMenu
      iconButtonElement={
        <IconButton>{iconMoreVert}</IconButton>
      }
      targetOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
    >
      <MenuItem primaryText="Edit account" onTouchTap={handleEditAccountClicked} />
      <MenuItem primaryText="Show analytics" onTouchTap={openGoogleAnalytics} />
      {user.disabled && <MenuItem primaryText="Enable account" onTouchTap={handleEnableUser} />}
      {!user.disabled && <MenuItem primaryText="Disable account" onTouchTap={handleDisableUser} />}
      <MenuItem primaryText="Delete account" onTouchTap={handleDelete} />
    </IconMenu>
  )
}

ContextMenu.propTypes = {
  children: PropTypes.any,
  user: PropTypes.object,
  onEditAccountClicked: PropTypes.func,
  onEnableUser: PropTypes.func,
  onDisableUser: PropTypes.func,
  onDeleteUser: PropTypes.func,
}

export default UserTable;



// WEBPACK FOOTER //
// ./src/components/UserTable/index.jsx